<template>
  <div class="personal-canvases-container h-full">
    <div ref="toolbarWrapper" class="sticky z-100">
      <Navbar />
      <CSearchFilters
        v-model:filters="filters"
        class="mt-3"
        :disabled="isFetchingCanvases"
        entity="canvas"
        hide-metadata-filters
        :placeholder="t('canvasUI.canvasSelector.canvases.searchByNameOrTag')"
        @update:filters="fetchCanvasesAfterFiltering"
        @update:search="handleSearch"
      />

      <SectionSelectorToolbar
        :are-all-selected="areAllSelected"
        :are-some-selected="areSomeSelected"
        :current-page="currentPage"
        :first-page-total-displayed-items="firstPageTotalDisplayedCanvases ?? 0"
        :loading="isFetchingCanvases"
        :page-size="pageSize"
        :total-items="availableCanvasesTotalCount"
        :total-selected="selectedCanvases.length"
        @page-first="fetchFirstChunk"
        @page-last="fetchLastChunk"
        @page-next="fetchNextChunk"
        @page-previous="fetchPreviousChunk"
        @toggle-select-all="toggleSelectAll"
      />
    </div>
    <div v-if="isFetchingCanvases" class="pt-3 overflow-auto" :style="{ height: wrapperHeight }">
      <NSkeleton v-for="n in 8" :key="n" class="w-full rounded" :class="{ 'mt-1': n > 1 }" :height="135" />
    </div>
    <CList
      v-else-if="orderedAvailableCanvases?.length"
      class="rounded-2 flex bg-base overflow-y-auto"
      :data="orderedAvailableCanvases"
      :height="parseInt(wrapperHeight)"
      :maxRowHeight="150"
      :selectedIds="selectedCanvases.map((c) => c.id)"
      @item:click="onCanvasSelect"
    >
      <template #item="{ item, isSelected, index }">
        <CanvasListItem
          :data="orderedAvailableCanvases"
          :filters="filters"
          hide-popularity-icons
          :index="index"
          :is-selected="isSelected"
          :item="item"
          :section="item"
          :selected-items="selectedCanvases"
        />
      </template>
    </CList>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject, type ComputedRef, type ToRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { NSkeleton } from 'naive-ui'

import CSearchFilters from '@lib/components/CSearchFilters/CSearchFilters.vue'
import CList from '@lib/components/CList/CList.vue'
import CanvasListItem from './CanvasListItem.vue'
import SectionSelectorToolbar from '@canvas-builder/components/ui/SectionList/SectionSelectorToolbar.vue'
import useAppStore from '@canvas-selector/stores/app'
import type { Canvas } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { AppProps } from '@canvas-selector/appTypes'
import Navbar from './Navbar.vue'
import { normalizeFilterParams } from '@lib/components/filters/filter.util'
import { useElementHeightResize } from '@lib/composables/useElementHeightResize'

const { t } = useI18n()
const { data, selectedCanvases, selectedItems } = useAppStore()

const appProps = inject<ToRefs<AppProps>>('appProps')
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const pageSize = computed(() => launchDarkly.value.canvas_selector_pageSize || 500)
const filters = ref<Record<string, any>>({})
const isFetchingCanvases = ref(false)
const search = ref('')
const currentPage = ref(1)
const availableCanvasesTotalCount = ref(0)
const firstPageTotalDisplayedCanvases = ref<number | undefined>()
const toolbarWrapper = ref<HTMLElement | null>(null)
const { wrapperHeight } = useElementHeightResize(toolbarWrapper, calculateWrapperHeight)
const orderedAvailableCanvases = computed(() => {
  return data.value?.canvases ?? []
})

const areAllSelected = computed(
  () => !!data.value?.canvases?.every((canvas) => selectedCanvases.value.some((c) => c.id === canvas.id)),
)
const areSomeSelected = computed(
  () =>
    !!(
      data.value?.canvases?.some((canvas) => selectedCanvases.value.some((c) => c.id === canvas.id)) &&
      !areAllSelected.value
    ),
)

const fetchCanvases = async () => {
  if (!appProps?.listCanvases) return

  isFetchingCanvases.value = true
  try {
    const response = await appProps.listCanvases.value({
      is_template: false,
      is_section: false,
      search: search.value,
      filters: normalizeFilterParams(filters.value),
      page: currentPage.value,
      page_size: pageSize.value,
    })
    data.value = { ...data.value, canvases: response.results }
    availableCanvasesTotalCount.value = response.count!
    firstPageTotalDisplayedCanvases.value = response.results?.length
  } catch (error) {
    console.error('Failed to fetch personal canvases', error)
  } finally {
    isFetchingCanvases.value = false
  }
}

const handleSearch = (newValue: string) => {
  search.value = newValue
  currentPage.value = 1
  fetchCanvases()
}

const fetchCanvasesAfterFiltering = () => {
  currentPage.value = 1
  fetchCanvases()
}

const fetchNextChunk = () => {
  currentPage.value++
  fetchCanvases()
}

const fetchPreviousChunk = () => {
  if (currentPage.value > 1) {
    currentPage.value--
    fetchCanvases()
  }
}

const fetchFirstChunk = () => {
  currentPage.value = 1
  fetchCanvases()
}

const fetchLastChunk = () => {
  if (availableCanvasesTotalCount.value > 0) {
    currentPage.value = Math.ceil(availableCanvasesTotalCount.value / pageSize.value)
    fetchCanvases()
  }
}

const onCanvasSelect = (canvas: Canvas) => {
  const index = selectedItems.value.findIndex((c) => c.id === canvas.id)
  if (index !== -1) {
    selectedItems.value.splice(index, 1)
  } else {
    selectedItems.value.push(canvas)
  }
}

const toggleSelectAll = () => {
  if (areAllSelected.value) {
    selectedItems.value = selectedItems.value.filter((c) => !data.value?.canvases?.some((canvas) => c.id === canvas.id))
  } else {
    selectedItems.value = [
      ...selectedItems.value,
      ...(data.value?.canvases?.filter(
        (c) => !selectedItems.value.some((selectedCanvas) => selectedCanvas.id === c.id),
      ) ?? []),
    ]
  }
}

function calculateWrapperHeight(height: number) {
  return `${(toolbarWrapper.value?.parentElement?.parentElement?.offsetHeight || 0) - (height || 0) - 20}px`
}

onMounted(fetchCanvases)
</script>
